import { Button } from '@redfoxwp/core-components/components/button';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { FlexItem } from '@redfoxwp/core-components/components/flex-item';
import { Modal, type ModalStrings } from '@redfoxwp/core-components/components/modal';
import { Select, type SelectOption } from '@redfoxwp/core-components/components/select';
import { __ } from '@wordpress/i18n';
import React, { useEffect, useState } from 'react';

import { useGlobalTabsContext } from '@/admin/contexts';
import type { RedFoxTab } from '@/admin/hooks/use-global-tabs';
import { useGlobalTabs } from '@/admin/hooks/use-global-tabs';

import type { GlobalTab } from '../../types';
import type { TabModalProps } from './types';

export const TabModal: React.FC<TabModalProps> = ({
  isOpen,
  mode,
  editingTab,
  onClose,
  onSave,
  onError,
}) => {
  const [formData, setFormData] = useState<Partial<GlobalTab>>({
    title: '',
    content: '',
    priority: 10,
    enabled: true,
    type: 'redfoxwp_tab',
    source: 'redfox',
    redfoxwp_tab_id: 0,
  });

  const [redFoxTabs, setRedFoxTabs] = useState<RedFoxTab[]>([]);
  const [isLoadingTabs, setIsLoadingTabs] = useState(false);

  // Use the global tabs hook for fetching RedFox tabs
  const { fetchRedFoxTabs } = useGlobalTabs({
    autoLoad: false, // Don't auto-load global tabs, we only need the fetchRedFoxTabs method
    ...(onError && { onError }),
  });

  // Get current global tabs from context to filter out duplicates
  const { tabs: globalTabs } = useGlobalTabsContext();

  const loadRedFoxTabs = React.useCallback(async () => {
    setIsLoadingTabs(true);
    try {
      const tabsData = await fetchRedFoxTabs({
        postsPerPage: -1,
        orderBy: 'date',
        order: 'DESC',
      });
      setRedFoxTabs(tabsData);
    } catch {
      setRedFoxTabs([]); // Set empty array on error
      // Error handling is already done in the hook
    }
    setIsLoadingTabs(false);
  }, [fetchRedFoxTabs]);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && editingTab) {
        setFormData(editingTab);
      } else {
        // Reset form for new tab
        setFormData({
          title: '',
          content: '',
          priority: 10,
          enabled: true,
          type: 'redfoxwp_tab',
          source: 'redfox',
          redfoxwp_tab_id: 0,
        });
      }

      // Load RedFox tabs
      loadRedFoxTabs();
    }
  }, [isOpen, mode, editingTab, loadRedFoxTabs]);

  // Filter out already added tabs (except when editing the current tab)
  const availableRedFoxTabs = React.useMemo(() => {
    // Get list of already added RedFox tab IDs
    const addedTabIds = globalTabs
      .filter((tab: GlobalTab) => tab.type === 'redfoxwp_tab' && tab.redfoxwp_tab_id)
      .map((tab: GlobalTab) => tab.redfoxwp_tab_id);

    // When editing, allow the current tab's ID to be available
    const currentEditingTabId =
      mode === 'edit' && editingTab?.redfoxwp_tab_id ? editingTab.redfoxwp_tab_id : null;

    return redFoxTabs.filter((tab) => {
      // Include if not already added, or if it's the currently editing tab
      return !addedTabIds.includes(tab.id) || tab.id === currentEditingTabId;
    });
  }, [redFoxTabs, globalTabs, mode, editingTab]);

  // Convert available RedFox tabs to Select options
  const selectOptions: SelectOption[] = React.useMemo(
    () =>
      availableRedFoxTabs.map((tab) => ({
        value: tab.id.toString(),
        label: tab.title,
      })),
    [availableRedFoxTabs]
  );

  const handleRedFoxTabChange = (value: string | string[], _selectedOptions: SelectOption[]) => {
    // Since we're using single select mode, value should be a string
    const selectedValue = Array.isArray(value) ? value[0] : value;
    const tabId = parseInt(selectedValue ?? '', 10);
    const selectedTab = availableRedFoxTabs.find((tab) => tab.id === tabId);

    setFormData((prev: Partial<GlobalTab>) => ({
      ...prev,
      redfoxwp_tab_id: tabId,
      title: selectedTab ? selectedTab.title : (prev.title ?? ''),
      content: selectedTab ? selectedTab.content : '',
    }));
  };

  const handleSave = () => {
    // Validate that a RedFox tab is selected
    if (!formData.redfoxwp_tab_id || formData.redfoxwp_tab_id === 0) {
      onError?.(__('Please select a RedFox tab', 'redfox-tab-manager'));
      return;
    }

    // Get the selected RedFox tab data
    const selectedRedFoxTab = availableRedFoxTabs.find(
      (tab) => tab.id === formData.redfoxwp_tab_id
    );

    const newTab: GlobalTab = {
      id: formData.id ?? `tab_${Date.now()}`,
      title: selectedRedFoxTab ? selectedRedFoxTab.title : (formData.title ?? 'RedFox Tab'),
      content: selectedRedFoxTab ? selectedRedFoxTab.content : (formData.content ?? ''),
      priority: formData.priority ?? 10,
      enabled: formData.enabled !== false,
      type: 'redfoxwp_tab',
      source: 'redfox',
      redfoxwp_tab_id: formData.redfoxwp_tab_id ?? 0,
    };

    onSave(newTab);
  };

  // Dynamic button text based on mode
  const buttonText =
    mode === 'edit' ? __('Save Changes', 'redfox-tab-manager') : __('Add', 'redfox-tab-manager');

  // Check if a tab is selected for button state
  const isTabSelected = formData.redfoxwp_tab_id && formData.redfoxwp_tab_id !== 0;

  // Footer with action button
  const footer = (
    <Button onClick={handleSave} variant='solid' size='medium' disabled={!isTabSelected}>
      {buttonText}
    </Button>
  );

  // Create localized strings for the modal
  const modalStrings: ModalStrings = {
    closeModal: __('Close modal', 'redfox-tab-manager'),
  };

  return (
    <Modal
      isOpen={isOpen}
      title={
        mode === 'edit' ? __('Edit Tab', 'redfox-tab-manager') : __('Add Tab', 'redfox-tab-manager')
      }
      onClose={onClose}
      footer={footer}
      strings={modalStrings}
      styleProps={{
        overflow: 'visible', // Allow dropdowns to overflow the modal
      }}
    >
      <Flex direction='column' gap='10px'>
        <FlexItem>
          <label>{__('Select a tab', 'redfox-tab-manager')}</label>
        </FlexItem>
        <FlexItem>
          <Select
            options={selectOptions}
            value={formData.redfoxwp_tab_id ? formData.redfoxwp_tab_id.toString() : ''}
            onChange={handleRedFoxTabChange}
            placeholder={__('Select a RedFox Tab…', 'redfox-tab-manager')}
            searchable={true}
            clearable={true}
            disabled={isLoadingTabs}
            noOptionsText={(() => {
              if (isLoadingTabs) {
                return __('Loading RedFox tabs…', 'redfox-tab-manager');
              }
              if (redFoxTabs.length === 0) {
                return __('No RedFox tabs available.', 'redfox-tab-manager');
              }
              return __('All RedFox tabs have already been added.', 'redfox-tab-manager');
            })()}
            size='medium'
            styleProps={{
              zIndex: 99999999, // Higher than modal z-index (9999999) + backdrop safety margin
              maxHeight: '400px', // Increase max height
            }}
          />
        </FlexItem>
      </Flex>
    </Modal>
  );
};
